import React from 'react';
import { Button, Col, Form, Row, Tag, notification } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { FilterConfig, FilterValues } from 'types';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from 'components/Auth/Auth';
import ButtonLink from 'components/theme_components/ButtonLink';
import ConfirmModal from 'components/theme_components/ThirdParty/ConfirmModal';
import DataGrid from 'components/theme_components/DataGrid';
import ExportXLS from 'components/theme_components/ExportXLS';
import type { FormInstance } from 'antd/lib/form';
import HeroTitle from 'components/theme_components/HeroTitle';
import TaskSButtonSFS from 'pages/workflow/workflowSFS/TasksButtonSFS';
import { findFinCTByAnyRefOrStatusOrDate } from 'services/fincontract';
import { findPropByAnyRefOrStatusOrDate } from 'services/proposal';
import { toCurrency } from 'utils/utils';
import { useQuery, useQueryClient } from 'react-query';
import GetLabel from 'DisplayFramework/components/GetLabel';
import {
  findPaymentByAnyRefOrStatusOrDate,
  FindPaymentByAnyRefOrStatusOrDateParams,
} from 'services/PaymentServices';

export type IncidentpaymentTabProps = {
  form: FormInstance;
  color?: string;
  tpauthid: number;
  tpid?: number;
  itemsCompanyHierarchyTree: any[];
  isActive?: boolean; // New prop to track if tab is active
  onTabActivated?: () => void; // Callback when tab becomes active
};

const IncidentpaymentTab = (props: IncidentpaymentTabProps) => {
  const {
    form,
    color,
    tpauthid,
    tpid,
    itemsCompanyHierarchyTree,
    isActive,
    onTabActivated,
  } = props;
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [filterForm] = Form.useForm();
  const { isInspect } = useContext(AuthContext);
  const [modalVisibleConfirm, setmodalVisibleConfirmVisible] = useState(false);

  const initialSearchParams: FindPaymentByAnyRefOrStatusOrDateParams = {
    cireference: '',
    ctreference: '',
    datemax: '',
    datemin: '',
    payallstat: '',
    paydocreference: '',
    paymode: '',
    payreference: '',
    paystatus: 'REJECT',
    sireference: '',
    tpname: '',
    tpreference: '',
    paydirection: '',
    currcode: '',
    paytype: '',
  };

  const [searchParams, setSearchParams] =
    useState<FindPaymentByAnyRefOrStatusOrDateParams>(initialSearchParams);

  const { data, refetch, isFetching } = useQuery(
    ['findPaymentByAnyRefOrStatusOrDate', searchParams],
    () =>
      findPaymentByAnyRefOrStatusOrDate(searchParams).then((res) => {
        return res.list || [];
      })
  );
  // Effect to handle tab activation
  useEffect(() => {
    if (isActive) {
      // Refetch data when tab becomes active
      refetch();
      onTabActivated?.();
    }
  }, [isActive, refetch, onTabActivated]);

  // Reset state when third party changes
  useEffect(() => {
    filterForm.resetFields();
    setSelectedRows([]);
  }, [tpauthid, tpid, filterForm]);

  const filterConfig: FilterConfig[] = [
    { key: 'paystatus', label: 'Type de rejet', type: 'select' },
    { key: 'paytype', label: 'Type  de transaction', type: 'select' },

    { key: 'rejectdate', label: 'Date de rejet', type: 'date' },

    { key: 'payrefrepresentation', label: 'Représentation', type: 'select' },
  ];

  const handleFilterSearch = useCallback((values: FilterValues) => {
    console.log('values::', values);
    const formattedValues = {
      ...initialSearchParams,
    };
    setSearchParams({ ...formattedValues });
  }, []);

  const handleFilterReset = useCallback(() => {
    filterForm.resetFields();
    // Reset but maintain the REFUSE filter
    setSearchParams({ ...initialSearchParams });
  }, [filterForm]);

  const columns = [
       { dataIndex: 'payrejectreason', key: 'payrejectreason' },
   
    { dataIndex: 'payreference', key: 'payreference' },

    {
      dataIndex: 'paytype',
      key: 'paytype',
    },
    {
      dataIndex: 'payamount',
      key: 'payamount',
    },
      {
      dataIndex: 'paystatus',
      key: 'paystatus',
    },
   

    {
      dataIndex: 'payrefrepresentation',
      key: 'payrefrepresentation',
    },

    {
      dataIndex: 'paydaterepresentation',
      key: 'paydaterepresentation',
    },
  ];

  const colsXLS = [
    'propreference',
    'vin1',
    'vin2',
    'prcodelabel',
    'ctstatus',
    'refreason',
    'ctstatusdate',
    'ctepartdays',
    'ctfinancedamount',
  ];

  console.log('selectedRows in parent:', selectedRows);

  return (
    <Form form={form} className="bg-color-gray">
      <div className="bg-color-gray">
        <HeroTitle
          form={filterForm}
          color={color || 'purple'}
          title="Liste des Paiments refusées"
          display
          showFilter
          filterConfig={filterConfig}
          onFilterSearch={handleFilterSearch}
          onFilterReset={handleFilterReset}
          className="productlist"
          noTitleOverlay
          itemsCompanyHierarchyTree={itemsCompanyHierarchyTree}
        />
      </div>
      <div className="ut-title-page no-after">
        <Row justify="space-between" align="middle" className="mb-4 mt-6">
          <Col>
            <Button
              icon={<DeleteOutlined />}
              className={
                selectedRows.length !== 0
                  ? 'no-border-radius  filter-item ant-btn-outline-primary bg-color-hover'
                  : 'no-border-radius  filter-item '
              }
              disabled={!isInspect}
              onClick={() => {
                !isInspect && setmodalVisibleConfirmVisible(true);
              }}
            >
              <GetLabel label="deleteDemands" />
            </Button>
          </Col>

          <Col span={5} className="d-flex justify-end align-center">
            <ExportXLS
              data={data || []}
              filename="reservations"
              columns={colsXLS}
            />
          </Col>
        </Row>
        <Row className="w-100 mb-1 d-flex align-center">
          <Col span={24}>
            <Form.Item name="filteredData">
              <DataGrid
                columns={columns}
                dataSource={data || []}
                size="large"
                className="custom-data-grid"
                rowKey="payid"
                isLoading={isFetching}
                rowSelection={{
                  type: 'checkbox',
                  onChange: (selectedRowKeys, selectedRows) => {
                    setSelectedRows(selectedRows);
                  },
                  hideSelectAll: false,
                  columnWidth: 80,
                }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} sur ${total}`,
                }}
                scrollInput={{ x: 1000, y: 550 }}
              />
            </Form.Item>
          </Col>
        </Row>
        <ConfirmModal
          className="modalConfirmOrCancel"
          visible={modalVisibleConfirm}
          status="success"
          message="Demande d'acceptation."
          description="Votre demande a été bien prise en charge."
          onConfirm={() => {
            !isInspect && setmodalVisibleConfirmVisible(false);
          }}
          Confirmcomcode="close"
          onlyConfirm
        />
      </div>
    </Form>
  );
};

export default IncidentpaymentTab;
